<template>
    <v-card class="rounded_card gap__card">
        <v-card-text>
            <div class="gap__head">
                <h3 class="mb-0">Who gets cited instead?</h3>
                <span class="gap__pill gap__pill--outline ml-3">AI project</span>
            </div>
            <p class="gap__muted mt-1 mb-0">
                For the prompts this page does not cover, these pages were cited in the AI answers of your project (last 90 days). Audit one of them to see side by side what it does better.
            </p>

            <!-- prompts to check -->
            <div v-if="gapPrompts.length" class="gap__prompts">
                <span class="gap__eyebrow">Prompts without coverage</span>
                <div class="gap__chips">
                    <span
                        v-for="prompt in gapPrompts"
                        :key="prompt.key"
                        class="gap__chip"
                        :class="{ 'gap__chip--on': selected[prompt.key] }"
                        role="button"
                        tabindex="0"
                        :title="prompt.text"
                        @click="toggle(prompt)"
                        @keydown.enter.prevent="toggle(prompt)"
                        @keydown.space.prevent="toggle(prompt)">
                        <span class="gap__chiptype">{{ prompt.type === "focus" ? "Focus" : "Prompt" }}</span>
                        {{ shorten(prompt.text, 70) }}
                    </span>
                </div>
                <div class="gap__actions">
                    <v-btn depressed rounded color="#007a68" dark small :loading="citedLoading" :disabled="citedLoading || !selectedCount" @click="loadCited()">
                        <v-icon small left>mdi-magnify</v-icon>Show cited pages
                    </v-btn>
                    <span class="gap__muted">{{ selectedCount }} of {{ gapPrompts.length }} prompts selected</span>
                </div>
            </div>
            <p v-else class="gap__muted mt-3 mb-0">This page covers every relevant prompt — there is nothing to compare against.</p>

            <p v-if="error" class="gap__error">{{ error }}</p>

            <!-- cited pages -->
            <template v-if="citedLoaded">
                <div v-if="!cited.length" class="gap__empty">No other pages were cited for the selected prompts in the last 90 days — only tracked prompts carry citation data.</div>
                <div v-else class="gap__list">
                    <div v-for="row in cited" :key="row.url" class="gap__row">
                        <div class="gap__rowmain">
                            <span class="font-weight-bold">{{ row.domain }}</span>
                            <a :href="row.url" target="_blank" rel="noopener" class="gap__url" :title="row.url">{{ shorten(row.url, 90) }}</a>
                            <div class="gap__rowmeta">
                                <span class="gap__pill">{{ row.citations }} citation{{ row.citations === 1 ? "" : "s" }}</span>
                                <span class="gap__pill gap__pill--outline">{{ row.prompt_count }} prompt{{ row.prompt_count === 1 ? "" : "s" }}</span>
                                <span v-for="platform in row.platforms" :key="row.url + platform" class="gap__pill gap__pill--outline">{{ platform }}</span>
                            </div>
                        </div>
                        <div class="gap__rowaction">
                            <template v-if="gapRunFor(row.url)">
                                <span class="gap__pill" :class="statusClass(gapRunFor(row.url).status)">{{ statusLabel(gapRunFor(row.url)) }}</span>
                                <v-btn v-if="gapRunFor(row.url).status === 'done' && gapRunFor(row.url).competitor_result_id" text small color="#007a68" @click="showComparison(gapRunFor(row.url))">
                                    Show comparison
                                </v-btn>
                            </template>
                            <v-btn v-else depressed rounded small outlined color="#007a68" :disabled="starting || !$store.getters.canWriteAiSuite" @click="startGapAudit(row)">Audit &amp; compare · 1 credit</v-btn>
                        </div>
                    </div>
                </div>
            </template>

            <!-- earlier comparisons for this page -->
            <div v-if="gapRuns.length && !citedLoaded" class="gap__previous">
                <span class="gap__eyebrow">Competitor audits for this page</span>
                <div v-for="gapRun in gapRuns" :key="String(gapRun.run_id)" class="gap__prevrow">
                    <span class="gap__pill" :class="statusClass(gapRun.status)">{{ statusLabel(gapRun) }}</span>
                    <span class="gap__url" :title="gapRun.url">{{ shorten(gapRun.url, 80) }}</span>
                    <v-btn v-if="gapRun.status === 'done' && gapRun.competitor_result_id" text small color="#007a68" @click="showComparison(gapRun)">Show comparison</v-btn>
                </div>
            </div>

            <!-- comparison -->
            <div v-if="comparisonLoading" class="gap__loading">
                <v-progress-circular indeterminate color="#007a68" size="24"></v-progress-circular>
                <span class="gap__muted">Comparing the two audits …</span>
            </div>
            <div v-else-if="comparison" class="gap__compare">
                <div class="gap__comparehead">
                    <span class="gap__eyebrow">Your page vs. {{ theirsDomain }}</span>
                    <span class="gap__muted">{{ shorten(theirs.url, 90) }}</span>
                    <p v-if="!comparison.comparable" class="gap__note">The two audits ran on different analysis versions — scores are not directly comparable, the check comparison still is.</p>
                </div>

                <!-- the decisive line: does each page answer the prompt at all? -->
                <template v-if="comparison.prompt_coverage && comparison.prompt_coverage.length">
                    <span class="gap__eyebrow">Does the page answer the prompt?</span>
                    <table class="gap__table">
                        <thead>
                            <tr>
                                <th>Prompt</th>
                                <th>Your page</th>
                                <th>{{ theirsDomain }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in comparison.prompt_coverage" :key="'pc-' + row.prompt">
                                <td :title="row.prompt">{{ shorten(row.prompt, 90) }}</td>
                                <td :class="verdictClass(row.mine)">{{ verdictLabel(row.mine) }}</td>
                                <td :class="verdictClass(row.theirs)">{{ verdictLabel(row.theirs) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <p class="gap__verdict">{{ verdictText }}</p>

                <div class="gap__scores">
                    <div v-for="score in comparison.scores" :key="score.key" class="gap__scorerow">
                        <span class="gap__scorelabel">{{ score.label }}</span>
                        <span class="gap__scoreval" :style="{ color: scoreColor(score.mine) }">{{ fmt(score.mine) }}</span>
                        <span class="gap__scoresep">vs</span>
                        <span class="gap__scoreval" :style="{ color: scoreColor(score.theirs) }">{{ fmt(score.theirs) }}</span>
                        <span v-if="score.diff !== null && comparison.comparable" class="gap__diff" :class="score.diff > 0 ? 'gap__diff--theirs' : score.diff < 0 ? 'gap__diff--mine' : ''">
                            {{ score.diff > 0 ? "+" : "" }}{{ score.diff }}
                        </span>
                    </div>
                </div>

                <template v-if="comparison.advantages.length">
                    <span class="gap__eyebrow">Why they get cited</span>
                    <div v-for="adv in comparison.advantages" :key="'adv-' + adv.id" class="gap__adv">
                        <span class="gap__pill gap__pill--danger">{{ adv.my_status === "fail" ? "You fail" : "You warn" }}</span>
                        <div class="gap__advbody">
                            <span class="font-weight-bold">{{ adv.label }}</span>
                            <span v-if="adv.recommendation" class="gap__muted">{{ adv.recommendation }}</span>
                        </div>
                    </div>
                </template>
                <p v-else class="gap__muted mt-3 mb-0">No check passes on their page that fails on yours — the difference lies in the metrics below.</p>

                <template v-if="comparison.my_advantages.length">
                    <span class="gap__eyebrow">Where you are ahead</span>
                    <div class="gap__chips">
                        <span v-for="adv in comparison.my_advantages" :key="'mine-' + adv.id" class="gap__pill gap__pill--outline gap__pill--good">{{ adv.label }}</span>
                    </div>
                </template>
                <template v-if="comparison.shared_issues.length">
                    <span class="gap__eyebrow">Both pages struggle with</span>
                    <div class="gap__chips">
                        <span v-for="issue in comparison.shared_issues" :key="'shared-' + issue.id" class="gap__pill gap__pill--outline">{{ issue.label }}</span>
                    </div>
                </template>

                <span class="gap__eyebrow">Side by side</span>
                <table class="gap__table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Your page</th>
                            <th>{{ theirsDomain }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="metric in comparison.metrics" :key="metric.key">
                            <td>{{ metric.label }}</td>
                            <td :class="{ 'gap__cell--win': metric.better === 'mine', 'gap__cell--lose': metric.better === 'theirs' }">{{ fmtMetric(metric, metric.mine) }}</td>
                            <td :class="{ 'gap__cell--win': metric.better === 'theirs', 'gap__cell--lose': metric.better === 'mine' }">{{ fmtMetric(metric, metric.theirs) }}</td>
                        </tr>
                    </tbody>
                </table>
                <p class="gap__note mb-0">Same audit, same checks for both pages. Being cited also depends on authority and freshness signals the audit cannot see.</p>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
    import { fetchCitedPages, fetchGapRuns, compareGap, createRun, fetchRun } from "./seoAuditApi"

    const POLL_MS = 10000
    const MAX_PROMPTS = 10

    export default {
        name: "GapAnalysis",
        props: {
            run: { type: Object, required: true },
            result: { type: Object, required: true },
            companyId: { type: String, required: true }
        },
        data() {
            return {
                selected: {},
                cited: [],
                citedLoading: false,
                citedLoaded: false,
                gapRuns: [],
                starting: false,
                comparison: null,
                comparisonLoading: false,
                theirs: {},
                error: "",
                pollTimer: null,
                isDestroyed: false
            }
        },
        computed: {
            // prompts (and focus topics) this page does not cover — the candidates for the gap check
            gapPrompts() {
                const coverage = this.result && this.result.coverage && typeof this.result.coverage === "object" ? this.result.coverage : null
                if (!coverage) return []
                const out = []
                const seen = new Set()
                const push = (type, text, verdict) => {
                    const clean = typeof text === "string" ? text.trim() : ""
                    if (!clean || (verdict !== "missing" && verdict !== "partial")) return
                    const key = clean.toLowerCase()
                    if (seen.has(key)) return
                    seen.add(key)
                    out.push({ key: type + "|" + key, type, text: clean, verdict })
                }
                ;(Array.isArray(coverage.focus) ? coverage.focus : []).forEach((e) => e && push("focus", e.query, e.verdict))
                ;(Array.isArray(coverage.prompts) ? coverage.prompts : []).forEach((e) => e && push("prompt", e.prompt, e.verdict))
                return out.slice(0, MAX_PROMPTS)
            },
            selectedCount() {
                return this.gapPrompts.filter((p) => this.selected[p.key]).length
            },
            // plain-language reading of the comparison, derived server-side (topic / onpage / offpage)
            verdictText() {
                const v = this.comparison && this.comparison.verdict
                if (v === "topic")
                    return "The decisive difference is the topic: their page answers the prompt, yours does not. Covering the question on your page (or on a page that fits it) comes before any on-page fix."
                if (v === "onpage") return "Their page is measurably better on the factors below — the listed checks are where your page gives citations away."
                if (v === "offpage")
                    return "Your page is at least as good on every on-page factor. Their citations come from signals the audit cannot see — domain authority, links and mentions, or a closer topical fit with the prompt."
                return "The two pages trade wins across the factors below."
            },
            theirsDomain() {
                try {
                    return new URL(this.theirs.url).hostname.replace(/^www\./, "")
                } catch (e) {
                    return "competitor"
                }
            }
        },
        watch: {
            gapPrompts: {
                immediate: true,
                handler(list) {
                    const next = {}
                    list.forEach((p) => {
                        next[p.key] = this.selected[p.key] === undefined ? true : this.selected[p.key]
                    })
                    this.selected = next
                }
            }
        },
        mounted() {
            this.loadGapRuns()
        },
        beforeDestroy() {
            this.isDestroyed = true
            this.stopPolling()
        },
        methods: {
            toggle(prompt) {
                this.$set(this.selected, prompt.key, !this.selected[prompt.key])
            },
            shorten(value, max) {
                const text = String(value || "")
                return text.length > max ? text.slice(0, max - 1) + "…" : text
            },
            fmt(value) {
                return value === null || value === undefined ? "–" : value
            },
            fmtMetric(metric, value) {
                if (value === null || value === undefined) return "–"
                if (typeof value === "boolean") return value ? "yes" : "no"
                if (metric.key === "js_verdict") return { ok: "yes", partial: "partially", critical: "no" }[value] || value
                return value + (metric.unit || "")
            },
            verdictLabel(verdict) {
                return { covered: "answers it", partial: "partly", missing: "does not answer" }[verdict] || "not checked"
            },
            verdictClass(verdict) {
                return verdict === "covered" ? "gap__cell--win" : verdict === "missing" ? "gap__cell--lose" : ""
            },
            scoreColor(score) {
                if (score === null || score === undefined) return "#3d6d66"
                if (score >= 80) return "#007a68"
                if (score >= 60) return "#9a6700"
                return "#b32d2d"
            },
            statusClass(status) {
                return status === "done" ? "gap__pill--good" : status === "failed" ? "gap__pill--danger" : "gap__pill--pending"
            },
            statusLabel(gapRun) {
                if (gapRun.status === "done") return "Audited"
                if (gapRun.status === "failed") return "Failed"
                return "Auditing …"
            },
            gapRunFor(url) {
                return this.gapRuns.find((g) => g.url === url) || null
            },
            async loadGapRuns() {
                try {
                    const runs = await fetchGapRuns(this.companyId, { runId: String(this.run._id), resultId: String(this.result._id) })
                    if (this.isDestroyed) return
                    this.gapRuns = runs
                    if (runs.some((g) => g.status === "pending" || g.status === "processing")) this.startPolling()
                    else this.stopPolling()
                } catch (e) {
                    if (!this.isDestroyed) this.error = "Could not load earlier competitor audits."
                }
            },
            async loadCited() {
                const prompts = this.gapPrompts.filter((p) => this.selected[p.key]).map((p) => p.text)
                if (!prompts.length) return
                this.citedLoading = true
                this.error = ""
                try {
                    const data = await fetchCitedPages(this.companyId, { projectId: String(this.run.project_id), prompts, excludeUrl: this.result.url, limit: 8 })
                    if (this.isDestroyed) return
                    this.cited = Array.isArray(data.cited) ? data.cited : []
                    this.citedLoaded = true
                } catch (e) {
                    if (!this.isDestroyed) this.error = "Could not load the cited pages. Please try again."
                } finally {
                    if (!this.isDestroyed) this.citedLoading = false
                }
            },
            async startGapAudit(row) {
                if (this.starting || !this.$store.getters.canWriteAiSuite) return
                this.starting = true
                this.error = ""
                try {
                    const prompts = (row.prompts || []).slice(0, 10)
                    const data = await createRun(this.companyId, {
                        urls: [row.url],
                        name: "Gap check: " + row.domain,
                        projectId: String(this.run.project_id),
                        focus: prompts.slice(0, 5),
                        gapFor: { runId: String(this.run._id), resultId: String(this.result._id), prompts }
                    })
                    if (this.isDestroyed) return
                    if (data && data.error === "not_enough_credits") {
                        const available = Math.max(0, (Number(data.credits) || 0) - (Number(data.count) || 0))
                        this.error = "Not enough audit credits: " + available + " available, 1 needed."
                        return
                    }
                    if (!data || !data.run_id) {
                        this.error = (data && data.error) || "The competitor audit could not be started."
                        return
                    }
                    this.gapRuns = [{ run_id: data.run_id, status: "pending", url: row.url, prompts, competitor_result_id: null }].concat(this.gapRuns)
                    this.startPolling()
                } catch (e) {
                    if (!this.isDestroyed) this.error = (e && e.response && e.response.data && e.response.data.error) || "The competitor audit could not be started."
                } finally {
                    if (!this.isDestroyed) this.starting = false
                }
            },
            startPolling() {
                if (this.pollTimer || this.isDestroyed) return
                this.pollTimer = setInterval(() => this.pollGapRuns(), POLL_MS)
            },
            stopPolling() {
                if (this.pollTimer) clearInterval(this.pollTimer)
                this.pollTimer = null
            },
            async pollGapRuns() {
                const active = this.gapRuns.filter((g) => g.status === "pending" || g.status === "processing")
                if (!active.length) return this.stopPolling()
                let changed = false
                for (const gapRun of active) {
                    try {
                        const data = await fetchRun(this.companyId, String(gapRun.run_id))
                        if (this.isDestroyed) return
                        const status = data && data.run ? data.run.status : null
                        if (status && status !== gapRun.status) changed = true
                    } catch (e) {
                        // transient — try again on the next tick
                    }
                }
                if (changed) {
                    await this.loadGapRuns()
                    if (this.isDestroyed) return
                    const done = this.gapRuns.find((g) => g.status === "done" && g.competitor_result_id)
                    if (done && !this.comparison) this.showComparison(done)
                }
            },
            async showComparison(gapRun) {
                this.comparisonLoading = true
                this.error = ""
                try {
                    const data = await compareGap(this.companyId, {
                        runId: String(this.run._id),
                        resultId: String(this.result._id),
                        competitorRunId: String(gapRun.run_id),
                        competitorResultId: String(gapRun.competitor_result_id)
                    })
                    if (this.isDestroyed) return
                    this.comparison = data && data.comparison ? data.comparison : null
                    this.theirs = data && data.theirs ? data.theirs : {}
                    if (!this.comparison) this.error = "The comparison could not be built."
                } catch (e) {
                    if (!this.isDestroyed) this.error = "The comparison could not be loaded."
                } finally {
                    if (!this.isDestroyed) this.comparisonLoading = false
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .gap__card {
        box-shadow: 0 2px 18px rgba(0, 0, 0, 0.06) !important;
        margin-top: 20px;
    }
    .gap__head {
        display: flex;
        align-items: center;
    }
    .gap__eyebrow {
        display: block;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3d6d66;
        margin-top: 18px;
        margin-bottom: 8px;
    }
    .gap__muted {
        font-size: 13px;
        color: #3d6d66;
    }
    .gap__note {
        font-size: 12px;
        color: #3d6d66;
        margin-top: 10px;
    }
    .gap__error {
        margin-top: 12px;
        color: #b32d2d;
        font-size: 13px;
    }
    .gap__chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .gap__chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        padding: 4px 10px;
        border-radius: 999px;
        border: 1px solid #d4ede9;
        color: #3d6d66;
        cursor: pointer;
        user-select: none;
        max-width: 100%;
    }
    .gap__chip--on {
        background: #e0f5f2;
        color: #007a68;
        border-color: #9fd9cf;
    }
    .gap__chiptype {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }
    .gap__actions {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 12px;
    }
    .gap__pill {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 600;
        border-radius: 999px;
        padding: 3px 10px;
        color: #ffffff;
        background: #3d6d66;
    }
    .gap__pill--outline {
        background: transparent;
        border: 1px solid #d4ede9;
        color: #3d6d66;
    }
    .gap__pill--good {
        background: #e0f5f2;
        border-color: #d4ede9;
        color: #007a68;
    }
    .gap__pill--danger {
        background: #b32d2d;
    }
    .gap__pill--pending {
        background: #0077cc;
    }
    .gap__empty {
        margin-top: 14px;
        font-size: 13px;
        color: #3d6d66;
    }
    .gap__list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 14px;
    }
    .gap__row,
    .gap__prevrow {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border: 1px solid #eaf3f1;
        border-radius: 10px;
    }
    .gap__rowmain {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
        flex: 1 1 auto;
    }
    .gap__rowmeta {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .gap__rowaction {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 0 0 auto;
    }
    .gap__url {
        font-size: 12px;
        color: #3d6d66;
        text-decoration: none;
        word-break: break-all;
    }
    .gap__previous {
        margin-top: 6px;
    }
    .gap__prevrow {
        margin-top: 8px;
    }
    .gap__loading {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 18px;
    }
    .gap__compare {
        margin-top: 18px;
        padding-top: 18px;
        border-top: 1px solid #eaf3f1;
    }
    .gap__comparehead .gap__eyebrow {
        margin-top: 0;
    }
    .gap__verdict {
        margin: 14px 0 0;
        padding: 10px 12px;
        border-radius: 10px;
        background: #f3faf8;
        font-size: 13px;
    }
    .gap__scores {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 10px;
    }
    .gap__scorerow {
        display: grid;
        grid-template-columns: 1fr 48px 28px 48px 52px;
        align-items: baseline;
        gap: 8px;
        font-size: 13px;
    }
    .gap__scoreval {
        font-size: 18px;
        font-weight: 700;
        text-align: right;
    }
    .gap__scoresep {
        font-size: 11px;
        color: #3d6d66;
        text-align: center;
    }
    .gap__diff {
        font-size: 12px;
        font-weight: 600;
        color: #3d6d66;
    }
    .gap__diff--theirs {
        color: #b32d2d;
    }
    .gap__diff--mine {
        color: #007a68;
    }
    .gap__adv {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 8px 0;
        border-bottom: 1px solid #f1f6f5;
    }
    .gap__advbody {
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: 13px;
    }
    .gap__table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }
    .gap__table th {
        text-align: left;
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #3d6d66;
        padding: 6px 8px;
        border-bottom: 1px solid #eaf3f1;
    }
    .gap__table td {
        padding: 7px 8px;
        border-bottom: 1px solid #f1f6f5;
    }
    .gap__cell--win {
        color: #007a68;
        font-weight: 600;
    }
    .gap__cell--lose {
        color: #b32d2d;
    }
    .gap__chip:focus-visible,
    .gap ::v-deep .v-btn:focus-visible {
        outline: 2px solid #0d2622;
        outline-offset: 2px;
    }
</style>
